import {
  campaignPricingModeSchema,
  currencySchema,
  FORM_FIELD_KEY_PATTERN,
  MAX_FIELDS_PER_SCHEMA,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './errors.js';
import { minorAmountSchema, moneySchema } from './money.js';

export const campaignQuoteSchema = z.object({
  amount: moneySchema,
  unitAmount: moneySchema.nullable(),
  quantity: z.number().int().nullable(),
  breakdown: z.object({
    pricingMode: campaignPricingModeSchema,
    unitLabel: z.string().nullable(),
    lines: z.array(
      z.object({ quantity: z.number().int(), unitAmount: moneySchema, amount: moneySchema }),
    ),
    minAmount: moneySchema.nullable(),
    maxAmount: moneySchema.nullable(),
    suggestedAmounts: z.array(moneySchema),
  }),
});

export type CampaignQuote = z.infer<typeof campaignQuoteSchema>;

/**
 * The answers a quote is asked about, by field key. Only the campaign's
 * price-deciding answers are read — a choice's option value, or a tick — and
 * nothing is validated: a partial form matches no conditional price and is
 * quoted at the unconditional one. It travels in a query string, so a tick
 * arrives as the text `true` —
 * docs/decisions/0072-a-price-may-depend-on-the-answers-and-a-group-on-the-animal.md.
 */
export const quoteAnswersSchema = z
  .record(z.string().regex(FORM_FIELD_KEY_PATTERN), z.union([z.string().max(120), z.boolean()]))
  .refine((answers) => Object.keys(answers).length <= MAX_FIELDS_PER_SCHEMA);

export const quoteInputSchema = z.object({
  campaignPeriodId: z.uuid(),
  currency: currencySchema,
  /**
   * The institution the donation will be fulfilled at, when the donor picks one
   * — a dormitory prices a qurbani differently from the next. It must be an
   * active assignment of the period, or the quote refuses rather than quietly
   * pricing at the season's default; naming one on the public route discloses
   * only what the portal's own form must already show a donor choosing it —
   * docs/decisions/0068-a-price-may-be-overridden-per-assignment.md.
   */
  fulfillmentInstitutionId: z.uuid().optional(),
  quantity: z.coerce.number().optional(),
  amountMinor: minorAmountSchema.optional(),
  /**
   * The donor's answers. Only answers to fields the donation site shows are
   * honoured — one to an `adminOnly` or `portalVisible: false` field is dropped
   * before it can select a price — so the route prices exactly what the
   * portal's own form lets a donor ask for, and discloses nothing more.
   */
  answers: quoteAnswersSchema.optional(),
});

/**
 * The public price of one donation, declared here rather than inside the
 * campaign contract because the donation site needs it and may not carry an
 * administrator's contract to reach it. `campaignContract.quote` is this same
 * procedure, so there is still exactly one `GET /campaigns/quote` and one
 * handler for it.
 */
export const campaignQuoteContract = oc
  .route({ method: 'GET', path: '/campaigns/quote' })
  .errors({
    NOT_FOUND: commonErrors.NOT_FOUND,
    CONFLICT: commonErrors.CONFLICT,
    VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
    PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
  })
  .input(quoteInputSchema)
  .output(campaignQuoteSchema);
